/* RESPONSIBLE TEAM: team-standalone */

import Platform from './platform';
import type { IntegrationType } from '../constants';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type AppService from 'embercom/services/app-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { TicketField, type TicketFieldResponse, type FieldType } from '../data-fields/common';
import type Store from '@ember-data/store';
import { taskFor } from 'ember-concurrency-ts';

/**
 * Abstract base class for standardized platforms (HubSpot, Freshdesk) that use
 * the newer standardized Integration endpoints.
 */
export default abstract class StandardizedPlatform extends Platform {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;

  @tracked private loadedTicketFields: TicketField[] = [];
  @tracked private loadingDataFields = false;

  /**
   * Each platform must provide its specific IntegrationTypes
   */
  protected abstract get integrationTypes(): IntegrationType[];

  async loadRequiredData() {
    await this.loadDataFields();
  }

  async loadDataFields() {
    this.loadingDataFields = true;
    let appId = this.appService.app.id;

    // Wait for integrations to be loaded before fetching data fields
    if (taskFor(this.finStandaloneService.loadStandaloneIntegrations).isRunning) {
      await taskFor(this.finStandaloneService.loadStandaloneIntegrations).lastRunning;
    }

    // Fetch data fields from all integration types concurrently
    let fetchPromises = this.integrationTypes.map(async (integrationType) => {
      let integration = this.finStandaloneService.standaloneIntegrations.find(
        (int) => int.integrationType === integrationType,
      );

      if (!integration?.id) {
        this.loadingDataFields = false;
        return { user: [], conversation: [], company: [] };
      }

      let url = `/ember/standalone/integrations/${integrationType}/${integration.id}/available_fields?app_id=${appId}`;

      try {
        let response = await request(url);
        let data = await response.json();
        this.loadingDataFields = false;
        return {
          user: data.user || [],
          conversation: data.conversation || [],
          company: data.company || [],
        };
      } catch (error) {
        console.error(
          `Failed to load ${this.platformKey} data fields for ${integrationType}:`,
          error,
        );
        this.loadingDataFields = false;
        return { user: [], conversation: [], company: [] };
      }
    });

    let results = await Promise.all(fetchPromises);

    // Some platforms may use the same field mappings across all its integrations.
    // In this case, we should deduplicate the fields by ID before mapping to Ember field types.
    let allUserFields = this.uniqueById(results.flatMap((result) => result.user));
    let allTicketFields = this.uniqueById(results.flatMap((result) => result.conversation));
    let allCompanyFields = this.uniqueById(results.flatMap((result) => result.company));

    this.store.pushPayload('standalone/external-field-identifier', {
      'standalone/external-field-identifier': [
        ...allUserFields,
        ...allTicketFields,
        ...allCompanyFields,
      ].map((f) => {
        return {
          ...f,
          id: `${f.intercom_field_type}/${f.external_id}`,
        };
      }),
    });

    this.loadedTicketFields = allTicketFields.map((attr: any) => this.mapToTicketField(attr));
    this.loadingDataFields = false;
  }

  get isLoadingDataFields() {
    return this.loadingDataFields;
  }

  private uniqueById(items: any[]): any[] {
    let seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }

  private mapToTicketField(attr: any): TicketField {
    let response: TicketFieldResponse = {
      id: attr.id,
      name: attr.name,
      description: attr.intercom_cvda?.description || undefined,
      type: attr.type as FieldType,
      required: false,
    };

    if (attr.intercom_cvda) {
      response.intercom_cvda = {
        id: attr.intercom_cvda.id,
      };
    }

    return new TicketField(response);
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return this.userFields.any(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    return this.companyFields.any(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return this.loadedTicketFields.any(
      (ticketField) => `conversation.${ticketField.intercom_cvda?.id}` === attributeIdentifier,
    );
  }

  get ticketFields() {
    return this.loadedTicketFields;
  }

  get userFields() {
    return this.store
      .peekAll('standalone/external-field-identifier')
      .filter((field) => field.isUserField);
  }

  get companyFields() {
    return this.store
      .peekAll('standalone/external-field-identifier')
      .filter((field) => field.isCompanyField);
  }
}
